import type * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { textVariants } from "@corpuscomplete/web-ui/components/text";

const textareaVariants = cva(
  "flex field-sizing-content w-full outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-input bg-input/20 dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 resize-none rounded-md border px-2 py-2 text-sm focus-visible:ring-[2px] aria-invalid:ring-[2px] md:text-xs/relaxed placeholder:text-muted-foreground min-h-16",
        ghost:
          "border-0 bg-transparent px-0 py-0 shadow-none resize-none focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type TextVariant = VariantProps<typeof textVariants>["variant"];

interface TextareaProps
  extends React.ComponentProps<"textarea">, VariantProps<typeof textareaVariants> {
  /**
   * Apply text styling from the Text component's variants.
   * Useful with the "ghost" variant to make the textarea appear as a heading or other text element.
   */
  textVariant?: TextVariant;
}

function Textarea({ className, variant, textVariant, ...props }: TextareaProps) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(
        textareaVariants({ variant }),
        textVariant && textVariants({ variant: textVariant }),
        className,
      )}
      {...props}
    />
  );
}

export { Textarea, textareaVariants, type TextareaProps };
